// Deterministic, client-side, regex-based pre-send redaction filter (Axis 6).
//
// Complements the ADR-005 sensitivity-classification gate
// (data-classification.ts): that gate decides whether an email may be sent to
// the AI endpoint AT ALL (block the whole turn). This filter runs on content
// that IS being sent and strips out likely-sensitive substrings (email
// addresses, phone numbers, SSNs, credit-card numbers, and common
// secret/token shapes) before the request leaves the client.
//
// Deliberately pure, synchronous, and pattern-based only — no AI pass, no
// network call — so this stays a deterministic v1 and never becomes a second
// egress path of its own. Treat all input as untrusted (email/attachment
// content, per CLAUDE.md's prompt-injection hardening precedent) — this
// module only ever reads `text`, never acts on it as instructions.

interface RedactionRule {
  id: string;
  pattern: RegExp;
  placeholder: string;
}

// Order matters: more specific/narrow token shapes are matched BEFORE broader
// numeric patterns, so a JWT or "Bearer <token>" phrase is redacted as a
// whole before any of its characters could be reinterpreted by a later rule.
// Every pattern below is bounded (fixed-size quantifiers, no nested/unbounded
// repetition over the same characters) to avoid catastrophic-backtracking
// (ReDoS) on attacker-controlled email content.
const REDACTION_RULES: RedactionRule[] = [
  {
    id: "jwt",
    // Three dot-separated base64url segments — the shape of a JSON Web Token.
    pattern: /\b[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\b/g,
    placeholder: "[REDACTED:TOKEN]",
  },
  {
    id: "bearer-token",
    pattern: /\bBearer\s+[A-Za-z0-9._-]{20,}\b/gi,
    placeholder: "[REDACTED:TOKEN]",
  },
  {
    id: "api-key-prefixed",
    // Common vendor-prefixed secret shapes (OpenAI-style "sk-...", GitHub PAT).
    pattern: /\b(?:sk-[A-Za-z0-9]{16,}|ghp_[A-Za-z0-9]{30,})\b/g,
    placeholder: "[REDACTED:API_KEY]",
  },
  {
    id: "generic-secret-assignment",
    // "api_key: <value>", "password=<value>", etc. — a labelled assignment of
    // a long opaque value, regardless of vendor-specific prefix.
    pattern:
      /\b(?:api[_-]?key|secret(?:[_-]?key)?|password|passwd|access[_-]?token)\s*[:=]\s*['"]?[A-Za-z0-9_-]{8,}['"]?/gi,
    placeholder: "[REDACTED:SECRET]",
  },
  {
    id: "credit-card",
    // 13-19 digits, optionally grouped with single hyphen/space separators.
    // Pattern match only for v1 — no Luhn checksum validation (see CLAUDE.md
    // A3 / the Axis 6 plan: a fast, deterministic filter, not a validator).
    // Each optional separator sits strictly BETWEEN two digits (mandatory
    // trailing `\d`) so the match can never swallow a trailing space/hyphen
    // that follows the card number in the surrounding text.
    pattern: /\b\d(?:[ -]?\d){12,18}\b/g,
    placeholder: "[REDACTED:CREDIT_CARD]",
  },
  {
    id: "ssn",
    // US SSN shape: ###-##-####. Below the credit-card rule's minimum digit
    // count (9 < 13), so the two never compete for the same substring.
    pattern: /\b\d{3}-\d{2}-\d{4}\b/g,
    placeholder: "[REDACTED:SSN]",
  },
  {
    id: "email",
    pattern: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g,
    placeholder: "[REDACTED:EMAIL]",
  },
  {
    id: "phone",
    // Requires at least one separator between the first two digit groups, to
    // avoid over-matching arbitrary 10-digit sequences (order numbers,
    // tracking numbers, etc.) that aren't phone numbers. Covers common US/intl
    // formats: "123-456-7890", "(123) 456-7890", "123.456.7890",
    // "+1 123-456-7890". Uses a lookbehind instead of a leading `\b`: a `\b`
    // fails to anchor immediately before "(" or "+" when the preceding
    // character is also non-word (e.g. a space) — both sides of that position
    // are non-word, so there is no boundary — which silently excluded the
    // leading "(" / "+1 " from the match. `(?<![A-Za-z0-9])` allows the match
    // to start at those non-word prefix characters while still rejecting a
    // start mid-token (e.g. "id415-555-0182").
    pattern: /(?<![A-Za-z0-9])(?:\+\d{1,3}[-.\s])?\(?\d{3}\)?[-.\s]\d{3}[-.\s]?\d{4}\b/g,
    placeholder: "[REDACTED:PHONE]",
  },
];

/**
 * Deterministically redacts likely-sensitive substrings from `text` before it
 * is sent to the configured AI endpoint. Pure and synchronous — no network
 * call, no AI pass, no side effects. Applies each rule in
 * {@link REDACTION_RULES} in turn; because a rule's replacement text (e.g.
 * `[REDACTED:EMAIL]`) never itself matches an earlier or later pattern, a
 * substring redacted by one rule can never be re-matched by another. Text
 * with no matches is returned unchanged (same string value).
 */
export function redactSensitiveContent(text: string): string {
  if (!text) {
    return text;
  }
  let result = text;
  for (const rule of REDACTION_RULES) {
    result = result.replace(rule.pattern, rule.placeholder);
  }
  return result;
}
